import get from 'lodash/get';
import intersection from 'lodash/intersection';
import { sendLog } from '@lambdatest/frontend-logger';

import {
  AvailableCapabilities,
  BrowsersMap,
  TestTypes,
} from '../constants/views';
import { isCDPTest } from './utilityFunctions';

export const isCapabilitySet = ({
  capability,
  testType = '',
  type,
  valueType = 'boolean',
}) => {
  if (
    (testType == TestTypes.CYPRESS ||
      testType == TestTypes.HYPERTEST_CYPRESS) &&
    type == 'video'
  ) {
    return true;
  }
  const desiredCapabilities = getCapabilitiesData({ capability, testType });
  const capabilityKeys = Object.keys(desiredCapabilities || {}) || [];

  if (capabilityKeys?.length) {
    return (
      capabilityKeys.includes(type) &&
      (valueType === 'boolean'
        ? desiredCapabilities[type] !== false &&
          desiredCapabilities[type] !== 'false'
        : desiredCapabilities[type] !== '')
    );
  }
  // Fallback for all other test types.
  return true;
};

export const getAllCapabilities = (testDetails) => {
  let capabilities = testDetails['capability'];
  try {
    if (capabilities) {
      capabilities = JSON.parse(capabilities);
    }
  } catch (err) {
    // Parsing error, update the capabilities.
    capabilities = {};
    sendLog({
      logDetails: {
        componentName: 'Unable to verify HAR capabilities',
        error: String(err),
        componentStack: capabilities,
      },
      logLevel: 'Error',
      serviceName: 'MagicLeap - Dashboard',
    });
  }
  return capabilities;
};

export const getCapabilitiesData = ({ capability, testType = '' }) => {
  let desiredCapabilities = {};
  if (testType === TestTypes.CYPRESS || isCDPTest(testType)) {
    desiredCapabilities = capability;
  } else {
    desiredCapabilities = get(capability, 'desiredCapabilities', {});
  }
  return desiredCapabilities;
};

export const isMobileDevice = (testDetails) => {
  return (
    testDetails.category === 'android' ||
    testDetails.category === 'ios' ||
    testDetails.category === 'fireos'
  );
};

export const isTestForFireOs = (testDetails) => {
  return testDetails.category === 'fireos';
};

export const getBrowserInfo = ({ capabilities, testType }) => {
  const desiredCapabilities = getCapabilitiesData({
    capability: capabilities,
    testType,
  });
  const browser = get(desiredCapabilities, 'browserName', '').toLowerCase();
  const browserVersion = get(desiredCapabilities, 'version', '').toLowerCase();
  return { browser, browserVersion };
};

/**
 * Gets capability information per browser and version.
 * @param {string} capabilityName
 * @param {!Object<string, *>} testDetails
 * @returns {!Object<string, string>} Information whether capability is supported or not.
 */
export const getCapabilityInfoForBrowser = ({
  capabilities,
  capabilityName,
  testDetail,
  testType,
}) => {
  let isCapabilitySupported = true;
  if (
    capabilityName === AvailableCapabilities.CONSOLE &&
    !isCDPTest(testType)
  ) {
    if (testDetail.category === 'ios') {
      return {
        isSupported: true,
      };
    }
    let browserInfoText = '';
    const mobile = isMobileDevice(testDetail);
    if (!mobile) {
      const { browser = '', browserVersion = '' } = getBrowserInfo({
        capabilities,
        testType,
      });
      if (
        ![
          BrowsersMap.OPERA,
          BrowsersMap.FIREFOX,
          BrowsersMap.EDGE,
          BrowsersMap.CHROME,
        ].includes(browser)
      ) {
        browserInfoText =
          'Chrome, Firefox on 55 and above versions & Opera on 69';
      }
      if (browser === BrowsersMap.EDGE && parseInt(browserVersion <= 18)) {
        browserInfoText = 'Microsoft Edge on 80';
      }
      if (browser === BrowsersMap.FIREFOX && parseInt(browserVersion) < 55) {
        browserInfoText = 'Firefox on 55';
      }
      if (browser === BrowsersMap.OPERA && parseInt(browserVersion) < 26) {
        browserInfoText = 'Opera on 69';
      }
      if (browserInfoText) {
        return {
          isSupported: false,
          textToShow: `Console logs are only available in ${browserInfoText} and above versions`,
        };
      }
    }
  }
  return { isSupported: isCapabilitySupported, textToShow: '' };
};

export const isUnsecureEdgeVersion = (browser, version) => {
  const unsecureVersion = ['15.0', '16.0', '17.0', '18.0'];
  return (
    browser?.toLowerCase() === 'microsoftedge' &&
    unsecureVersion.includes(version)
  );
};

export const getBrowserSubVersion = ({ command, isDesktopTest }) => {
  if (isDesktopTest) {
    const itemValue = get(command, 'Value', {});
    const responseBodyStr = get(itemValue, 'responseBody', '');
    try {
      const responseBody = JSON.parse(responseBodyStr);
      const browserVersion =
        get(responseBody, ['value', 'browserVersion'], '') ||
        get(responseBody, ['value', 'capabilities', 'browserVersion'], '') ||
        get(responseBody, ['value', 'version'], '') ||
        get(responseBody, ['value', 'capabilities', 'version'], '');
      return browserVersion;
    } catch (error) {
      sendLog({
        logDetails: {
          componentName: 'Command Log - No Action',
          error: String(error),
          componentStack: responseBodyStr,
        },
        logLevel: 'Error',
        serviceName: 'MagicLeap - Dashboard',
      });
    }
  }
  return '';
};

/**
 * Get Command Logs body to be displayed to the user.
 * @param {string} value
 * @returns Command Log Body to be displayed to the user.
 */
export const getLongCommandLogBody = (value) => {
  let valueToShow = value;
  if (value?.length > 1000) {
    valueToShow = 'Body is too long to display.';
  }
  return valueToShow;
};

export const modifyAnnotationsData = (selectedFilters, isNewAnnotationFlow) => {
  const pageNumberArr = selectedFilters.map((filter) => {
    // to fetch page from reverse since there can be - in name as well
    const splitFilterValue = filter.split('-');
    const lastElement = splitFilterValue[splitFilterValue?.length - 1] + '';
    return lastElement?.split(',')?.map((num) => parseInt(num.trim(), 10));
  });

  // pageNumberArr = [[1,2], [1,2,5,6]]
  // these are different pages corresponding to each annotation command
  const flattenedPageNumberArray = pageNumberArr.flat();
  const uniquePageNumberSet = new Set(flattenedPageNumberArray);
  // Convert Set back to array to maintain order
  const uniqueOrderedPageNumber = Array.from(uniquePageNumberSet);
  // Sort the array for ordered numbers
  uniqueOrderedPageNumber.sort((a, b) => a - b);

  // removing page number and cmdType from string and sending the value in api
  const testContextFilterValue = selectedFilters.map((filter) => {
    const lastHyphenIdx = filter.lastIndexOf('-');

    let remainingStr = '';

    if (lastHyphenIdx !== -1) {
      const secondLastHyohenIdx = filter.lastIndexOf('-', lastHyphenIdx - 1);

      if (secondLastHyohenIdx !== -1) {
        remainingStr = filter.substring(0, secondLastHyohenIdx); // removed page number and cmd type
      }

      // remainingStr = ["LeetcodeWebsite2-info"]

      const modifiedStrLastHyphenIndex = remainingStr.lastIndexOf('-');
      if (isNewAnnotationFlow) {
        return remainingStr.substring(0, modifiedStrLastHyphenIndex);
      }
      if (modifiedStrLastHyphenIndex !== -1) {
        const modifiedString =
          remainingStr.substring(0, modifiedStrLastHyphenIndex) +
          ':' +
          remainingStr.substring(modifiedStrLastHyphenIndex + 1);

        return modifiedString;
      }
    }

    // If no second-to-last hyphen is found, return the original string
    return filter;
  });

  // Sample Annotation
  // selectedFilters = ["LeetcodeWebsite2-info-command-1"]
  // testContextFilterValue = ["LeetcodeWebsite2:info"]
  // uniqueOrderedPageNumber = [1]

  let testContextFilter = {};

  if (selectedFilters && selectedFilters?.length !== 0) {
    testContextFilter = {
      annotations: [...testContextFilterValue],
      pages: [...uniqueOrderedPageNumber],
    };
  }

  return testContextFilter;
};

export const ANNOTATION_SEPARATOR = '~';

export const ALLOWED_BROWSERS_FOR_ACCESSIBILITY = ['chrome', 'microsoftedge'];

export const isAccessibilityEnabledOnTest = ({
  capabilityKeyToUse = 'capability',
  testDetail,
  orgInfo,
  /* This will be set for shareable links */
  showAccessibility = false,
}) => {
  try {
    if (!testDetail) {
      return false;
    }
    if (testDetail?.isRealMobile) {
      return false;
    }
    let capability = '';
    if (testDetail?.[capabilityKeyToUse]) {
      try {
        capability = JSON.parse(testDetail[capabilityKeyToUse]);
      } catch (e) {
        const capabilityKey = Object.keys(testDetail[capabilityKeyToUse]);
        if (
          capabilityKey &&
          capabilityKey?.length &&
          capabilityKey?.length > 0
        ) {
          capability = testDetail[capabilityKeyToUse];
        } else {
          capability = {};
        }
      }
    }
    if (!capability) {
      return false;
    }
    const isAccessibilityCapabilitySet = isCapabilitySet({
      capability,
      testType: testDetail.type,
      type: 'accessibility',
    });
    // Check if accessibility is allowed based on test details feature flag (new flow)
    // This enables viewing past reports even when the plan is exhausted
    const isAccessibilityAllowedOnTest =
      testDetail?.feature_flag?.accessibilityAllowed;
    // Fall back to plan attribute check for backward compatibility (old flow)
    const planAttributes = orgInfo?.plan_attributes;
    const isAccessibilityPlanSet =
      showAccessibility ||
      (planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] &&
        planAttributes['AUTOMATION_ACCESSIBILITY_ENABLED'] === 1);
    // Capability is always required, then either new flow or old flow
    return isAccessibilityCapabilitySet && (isAccessibilityAllowedOnTest || isAccessibilityPlanSet);
  } catch {
    return false;
  }
};

export const isNewBuildFilterCompliant = (testDetail, filter, buildList) => {
  const { buildDetails = {}, buildId = '' } = testDetail || {};

  const isExistingBuild = buildList?.some((build) => build.buildId === buildId);

  const { status = '', name = '', username = '', buildType = '' } =
    buildDetails || {};

  const buildTags = buildDetails?.buildTags?.map((tag) => tag.toString()) || [];
  const testTags = testDetail?.testTags?.map((tag) => tag.toString()) || [];

  const {
    searchText = '',
    status: filteredStatus = [],
    username: filteredUsername = [],
    buildType: filteredBuildType = [],
    tag: filteredBuildTag = [],
    testTag: filteredTestTag = [],
  } = filter || {};

  const isSearchTextMatch =
    !searchText ||
    name
      ?.toLowerCase()
      .includes(
        searchText?.toLowerCase() ||
          name?.toLowerCase().includes(buildId.toLowerCase())
      );
  const isStatusMatch =
    filteredStatus?.length === 0 ||
    filteredStatus.includes(status?.toLowerCase());
  const isUsernameMatch =
    filteredUsername?.length === 0 ||
    filteredUsername.includes(username?.toLowerCase());

  const isBuildTypeMatch =
    filteredBuildType?.length === 0 ||
    filteredBuildType.includes(buildType?.toLowerCase());

  const isBuildTagMatch =
    !filteredBuildTag?.length ||
    intersection(buildTags, filteredBuildTag).length > 0;

  const isTestTagMatch =
    !filteredTestTag?.length ||
    intersection(testTags, filteredTestTag).length > 0;

  // if build is already present in the list,
  // then we don't need to check for some build filters
  // because they can't be present in update build sse event like testTags
  // also in build update sse will have status change only from it past values
  if (
    isSearchTextMatch &&
    isStatusMatch &&
    isUsernameMatch &&
    isBuildTypeMatch &&
    (!isExistingBuild ? isBuildTagMatch && isTestTagMatch : true)
  ) {
    return true;
  } else {
    return false;
  }
};

export const isNewTestFilterCompliant = (testDetail, filter) => {
  const {
    buildId = '',
    username = '',
    osId = '',
    browser = '',
    name = '',
    type: testType = '',
    testId = '',
    status = '',
    statusInd = '',
    buildDetails = {},
  } = testDetail || {};

  const testTags = testDetail?.testTags?.map((tag) => tag.toString()) || [];

  const { name: buildName = '' } = buildDetails || {};
  const statusToBeUsed = status || statusInd;

  const {
    build: filterBuild = [],
    browser: filterBrowser = [],
    buildName: filteredBuild = [],
    os: filteredOs = [],
    searchText = '',
    status: filteredStatus = [],
    username: filteredUsername = [],
    type: filteredTestType = [],
    tag: filteredTestTags = [],
  } = filter || {};

  const isTestTagMatch =
    !filteredTestTags?.length ||
    intersection(testTags, filteredTestTags).length > 0;

  const isBrowserMatch =
    filterBrowser?.length === 0 ||
    filterBrowser.includes(browser?.toLowerCase());
  const isBuildNameMatch =
    filteredBuild?.length === 0 ||
    filteredBuild.includes(buildName?.toLowerCase());
  const isOsMatch =
    filteredOs?.length === 0 || filteredOs.includes(osId?.toLowerCase());
  const isSearchTextMatch =
    !searchText ||
    name
      ?.toLowerCase()
      .includes(
        searchText?.toLowerCase() ||
          name?.toLowerCase().includes(testId?.toLowerCase())
      );
  const isStatusMatch =
    filteredStatus?.length === 0 ||
    filteredStatus.includes(statusToBeUsed?.toLowerCase());
  const isUsernameMatch =
    filteredUsername?.length === 0 ||
    filteredUsername.includes(username?.toLowerCase());
  const isTestTypeMatch =
    filteredTestType?.length === 0 ||
    filteredTestType.includes(testType?.toLowerCase());

  const isBuildIdMatch =
    filterBuild?.length === 0 ||
    (buildId && filterBuild.includes(buildId.toString()));

  if (
    isBrowserMatch &&
    isBuildNameMatch &&
    isOsMatch &&
    isSearchTextMatch &&
    isStatusMatch &&
    isUsernameMatch &&
    isBuildIdMatch &&
    isTestTypeMatch &&
    isTestTagMatch
  ) {
    return true;
  } else {
    return false;
  }
};

export const isLiveInteractionCapabilitySet = ({
  capabilityKeyToUse = 'capability',
  testDetails,
}) => {
  try {
    let capability = '';
    if (testDetails?.[capabilityKeyToUse]) {
      capability = JSON.parse(testDetails?.[capabilityKeyToUse]);
      const desiredCapabilities = getCapabilitiesData({
        capability,
        testType: 'selenium',
      });
      const liveInteractionCapability = Object.keys(
        desiredCapabilities
      ).includes('liveInteraction');
      if (liveInteractionCapability) {
        return (
          desiredCapabilities['liveInteraction'] === false ||
          desiredCapabilities['liveInteraction'] === 'false'
        );
      }
      return false;
    }
  } catch (error) {
    sendLog({
      logDetails: {
        componentName: 'Live Interaction Capability',
        error: String(error),
        componentStack: testDetails,
      },
      logLevel: 'Error',
      serviceName: 'MagicLeap - Dashboard',
    });
    return false;
  }
};

export const getFlakyRequestBody = (testList) => {
  const testString = testList.join("','");
  const body = JSON.stringify({
    query: {
      size: 0,
      aggs: {
        rows: {
          terms: {
            field: 'test_id.keyword',
            size: 100,
            include: {
              partition: 0,
              num_partitions: 1,
            },
          },
          aggs: {
            columns: {
              filter: {
                match_all: {},
              },
              aggs: {
                top_details: {
                  top_hits: {
                    _source: ['flake_rate'],
                    size: 1,
                  },
                },
              },
            },
          },
        },
      },
    },
    meta: {
      type: 'table',
      isEncoded: false,
    },
    index: 'testInfoAutomationFTD',
    filters: `is_flaky IS true AND test_id IN ('${testString}')`,
  });
  return body;
};
